/**
 * BgtOvrlEmpGrnamtDialog.tsx
 * ------------------------------------------------------------
 * 총괄 조합비 > 우정실무원 교부금 배정 모달 내용물
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";

interface Props {
  ovrlMbsdsExpndDocId: string;
  setEmpGrnamtModalOpen: Function;
  setEmpGrnamtSum: Function; // 우정실무원 교부금 합계(= 조합비 합계 금액)
  setImplAmt: Function; // 집행금액(= 조합비 합계 금액)
}
const toNum = (v: any) => {
  const n = Number(Util.uncomma(String(v ?? "0")) || "0");
  return Number.isFinite(n) ? n : 0;
};

/** 천원단위 올림 */
const ceilToThousand = (n: number) => Math.ceil(n / 1000) * 1000;

const BgtOvrlEmpGrnamtDialog = ({
  ovrlMbsdsExpndDocId,
  setEmpGrnamtModalOpen,
  setEmpGrnamtSum,
  setImplAmt,
}: Props) => {
  const [dataArr, setDataArr] = React.useState<any[]>([]);

  // 합계
  const [sumMbsdsSumAmt, setSumMbsdsSumAmt] = React.useState<string>("0");
  const [sumOperAmt, setSumOperAmt] = React.useState<string>("0");
  const [sumDtbnAmt, setSumDtbnAmt] = React.useState<string>("0");
  const [sumDtbnNopeCnt, setSumDtbnNopeCnt] = React.useState<string>("0");
  const [sumDtbnPstofcAmt, setSumDtbnPstofcAmt] = React.useState<string>("0");
  const [sumDtbnSumAmt, setSumDtbnSumAmt] = React.useState<string>("0");

  // =========================
  // 퍼센트 픽스: 로우 순서 기반 운영%
  // =========================
  const OPER_RT_BY_ROW_INDEX: number[] = [
    30, // 서울지방본부
    30, // 경인지방본부
    30, // 부산지방본부
    30, // 충청지방본부
    28, // 전남지방본부
    30, // 경북지방본부
    15, // 전북지방본부
    14, // 강원지방본부
    52, // 제주우체국
    48, // 서귀포우체국
    52, // 제주우편집중국
    33, // 우정인재개발원
    33, // 우정정보관리원
    33, // 우정사업조달센터
  ];

  // =========================
  // 반올림(올림) 적용 시작 행: "9번째 행(제주)"부터
  // =========================
  const isCeilDtbnRow = (index: number) => index >= 8;

  // =========================
  // 합계 재계산
  // =========================
  const recalcSums = (next: any[]) => {
    setSumMbsdsSumAmt(next.reduce((acc, r) => acc + toNum(r.mbsdsSumAmt), 0));
    setSumOperAmt(next.reduce((acc, r) => acc + toNum(r.operAmt), 0));
    setSumDtbnAmt(next.reduce((acc, r) => acc + toNum(r.dtbnAmt), 0));
    setSumDtbnNopeCnt(next.reduce((acc, r) => acc + toNum(r.dtbnNopeCnt), 0));
    setSumDtbnPstofcAmt(next.reduce((acc, r) => acc + toNum(r.dtbnPstofcAmt), 0));
    setSumDtbnSumAmt(next.reduce((acc, r) => acc + toNum(r.dtbnSumAmt), 0));
  };

  // =========================
  // 행 계산 (퍼센트 픽스 + 반올림 규칙 핵심)
  // =========================
  const calcRowByRule = (row: any, index: number) => {
    const total = Number(row.mbsdsSumAmt) === 0 ? "" : row.mbsdsSumAmt; // 화면용 값
    const cnt = Number(row.dtbnNopeCnt) === 0 ? "" : row.dtbnNopeCnt; // 집중국 인원(입력, 0이상 정수)

    // 1) 집중국금액 = 인원 * 5,000
    const pstofcAmt = cnt * 5000;

    // 2) base = 총액 - 집중국금액 (0 미만 방지)
    let base = total - pstofcAmt;
    if (base < 0) base = 0;

    // 3) 운영%는 index 기반으로 강제
    const operRt = OPER_RT_BY_ROW_INDEX[index];
    const dtbnRt = 100 - operRt;

    let operAmt = 0;
    let dtbnAmt = 0;

    // 4) 반올림(올림) 규칙
    if (isCeilDtbnRow(index)) {
      // [9행(제주)부터] 교부금을 천원단위 올림 -> 운영금은 차액
      const dtbnRaw = (base * dtbnRt) / 100;
      dtbnAmt = ceilToThousand(dtbnRaw);

      // 교부금이 base를 넘지 않도록 캡(특히 base < 1000일 때)
      if (dtbnAmt > base) dtbnAmt = base;

      operAmt = base - dtbnAmt;
    } else {
      // [1~8행] 기존: 운영금 floor -> 교부금 차액
      operAmt = Math.floor((base * operRt) / 100);
      dtbnAmt = base - operAmt;
    }

    // 5) 총배정액 = 교부금 + 집중국금액
    const dtbnSumAmt = dtbnAmt + pstofcAmt;

    return {
      ...row,

      // %는 강제
      operRt: operRt,
      dtbnRt: dtbnRt,

      // 입력 정규화
      mbsdsSumAmt: total,
      dtbnNopeCnt: cnt,

      // 자동 산출
      dtbnPstofcAmt: pstofcAmt,
      operAmt: operAmt,
      dtbnAmt: dtbnAmt,
      dtbnSumAmt: dtbnSumAmt,
    };
  };

  // =========================
  // 조회 (가져오면 현재 규칙으로 정규화)
  // =========================
  React.useEffect(() => {
    if (Util.isEmpty(ovrlMbsdsExpndDocId)) return;

    AppContext.showSpinner();
    Axios.dataAccess({
      url: "system/bgt/ovrl/expnd/select-ovrl-emp-grnamt-list.do",
      methodType: "post",
      paramType: "json",
      paramData: { ovrlMbsdsExpndDocId },
      onSuccessFn: (res: any) => {
        const list = res.result.list || [];
        const normalized = list.map((r: any, idx: number) => calcRowByRule(r, idx));

        setDataArr(normalized);
        recalcSums(normalized);

        AppContext.hideSpinner();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovrlMbsdsExpndDocId]);

  // =========================
  // 입력 핸들러
  // - 총액/인원만 입력 받아도 나머지 자동 산출
  // =========================
  const updateRow = (index: number, patch: Partial<any>) => {
    const next = [...dataArr];
    const merged = { ...next[index], ...patch };
    next[index] = calcRowByRule(merged, index);

    setDataArr(next);
    recalcSums(next);
  };

  // 총액 변경(입력)
  const onChangeMbsdsSumAmt = (index: number, v: string) => {
    updateRow(index, { mbsdsSumAmt: Util.uncomma(v) });
  };

  // 집중국 인원 변경(입력) -> 인원 기반으로 집중국금액도 자동 산출
  const onChangeDtbnNopeCnt = (index: number, v: string) => {
    updateRow(index, { dtbnNopeCnt: Util.uncomma(v) });
  };

  // =========================
  // 저장/닫기
  // =========================
  const closeModal = () => {
    setEmpGrnamtModalOpen(false);
    setEmpGrnamtSum(sumMbsdsSumAmt); // 우정실무원 교부금 합계(= 조합비 합계)
    setImplAmt(sumMbsdsSumAmt); // 집행금액(= 조합비 합계)
  };

const save = () => {
  if (Util.isEmpty(ovrlMbsdsExpndDocId)) return;

  // =========================
  // 저장 전 검증(계산 포함)
  // - 음수/NaN/인원 정수/집중국금액>총액/총배정액>총액/계산 불일치
  // =========================
  const invalid = dataArr.some((r: any, idx: number) => {
    const orgNm = r.grnamtOrgNm ?? `${idx + 1}행`;

    const total = Number(Util.uncomma(String(r.mbsdsSumAmt === "" ? "0" : r.mbsdsSumAmt)) || "0");
    const cnt = Number(Util.uncomma(String(r.dtbnNopeCnt === "" ? "0" : r.dtbnNopeCnt)) || "0");

    // 인원은 정수만 허용
    if (!Number.isFinite(cnt) || !Number.isInteger(cnt) || cnt < 0) {
      AppContext.openAlert({
        title: "입력값 오류",
        msg: `${orgNm}의 [집중국 인원]은 0 이상 정수만 입력 가능합니다.`,
        icon: "warning",
      });
      return true;
    }

    // 총액 검증
    if (!Number.isFinite(total) || total < 0) {
      AppContext.openAlert({
        title: "입력값 오류",
        msg: `${orgNm}의 [조합비총액]은 0 이상 숫자만 입력 가능합니다.`,
        icon: "warning",
      });
      return true;
    }

    // 현재 규칙대로 재계산(저장 직전 확정값)
    const pstofcAmt = cnt * 5000;
    const operRt = OPER_RT_BY_ROW_INDEX[idx];
    const dtbnRt = 100 - operRt;

    let base = total - pstofcAmt;
    if (base < 0) base = 0;

    let operAmt = 0;
    let dtbnAmt = 0;

    if (isCeilDtbnRow(idx)) {
      const dtbnRaw = (base * dtbnRt) / 100;
      dtbnAmt = ceilToThousand(dtbnRaw);
      if (dtbnAmt > base) dtbnAmt = base;
      operAmt = base - dtbnAmt;
    } else {
      operAmt = Math.floor((base * operRt) / 100);
      dtbnAmt = base - operAmt;
    }

    const dtbnSumAmt = dtbnAmt + pstofcAmt;

    // 논리 체크 1) 집중국금액이 총액을 넘으면 안됨
    if (pstofcAmt > total) {
      AppContext.openAlert({
        title: "배정금액 확인",
        msg:
          `${orgNm}의 [집중국 금액]이 조합비총액을 초과합니다.`,
        icon: "warning",
      });
      return true;
    }

    // 논리 체크 2) 총배정액이 총액을 넘으면 안됨
    if (dtbnSumAmt > total) {
      AppContext.openAlert({
        title: "배정금액 확인",
        msg:
          `${orgNm}의 [총배정액]이 조합비총액을 초과합니다.`,
        icon: "warning",
      });
      return true;
    }

    // 논리 체크 3) 운영+교부 = (총액-집중국) 일치
    if (operAmt + dtbnAmt !== base) {
      AppContext.openAlert({
        title: "계산값 불일치",
        msg:
          `${orgNm}의 계산값이 일치하지 않습니다.`,
        icon: "warning",
      });
      return true;
    }

    // 논리 체크 4) 총배정액 = 교부 + 집중국 일치
    if (dtbnSumAmt !== dtbnAmt + pstofcAmt) {
      AppContext.openAlert({
        title: "계산값 불일치",
        msg: `${orgNm}의 총배정액 계산이 올바르지 않습니다.`,
        icon: "warning",
      });
      return true;
    }

    return false;
  });

  if (invalid) return;

  AppContext.showSpinner();
  Axios.dataAccess({
    url: "system/bgt/ovrl/expnd/save-ovrl-emp-grnamt.do",
    methodType: "post",
    paramType: "json",
    paramData: {
      ovrlMbsdsExpndDocId,
      sumMbsdsSumAmt: sumMbsdsSumAmt,
      dataList: dataArr.map((r) => ({
        mbsdsEmpGrnamtId: r.mbsdsEmpGrnamtId,
        ovrlMbsdsExpndDocId,
        grnamtOrgCd: r.grnamtOrgCd,

        mbsdsSumAmt: r.mbsdsSumAmt === "" ? "0" : Util.uncomma(r.mbsdsSumAmt),
        operRt: Util.uncomma(r.operRt),
        operAmt: Util.uncomma(r.operAmt),
        dtbnRt: Util.uncomma(r.dtbnRt),
        dtbnAmt: Util.uncomma(r.dtbnAmt),
        dtbnNopeCnt: r.dtbnNopeCnt === "" ? "0" : Util.uncomma(r.dtbnNopeCnt),
        dtbnPstofcAmt: Util.uncomma(r.dtbnPstofcAmt),
        dtbnSumAmt: Util.uncomma(r.dtbnSumAmt),
      })),
    },
    onSuccessFn: () => {
      AppContext.hideSpinner();
      closeModal();
    },
  });
};


  // render
  return (
    <div className="w100p dpFlx jcC">
      <div className="w900">
        <div className="bd1 bdcEDEDED bdrd5 ofH">
          <table className="w100p tbl-collapse tlF">
            <colgroup>
              <col className="w120" /> {/* 지방본부별 */}
              <col className="w130" /> {/* 조합비총액(금액) */}
              <col className="w40" /> {/* 운영% */}
              <col className="w120" /> {/* 운영금액 */}
              <col className="w40" /> {/* 교부% */}
              <col className="w120" /> {/* 교부금액 */}
              <col className="w60" /> {/* 집중국 인원 */}
              <col className="w120" /> {/* 집중국 금액 */}
              <col className="w130" /> {/* 총배정액(읽기) */}
            </colgroup>

            <thead>
              <tr className="h30">
                <th rowSpan={2} className="bd1 bdcEDEDED bgcF1F8FF taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc2c3e50 wsNw">지방본부별</span>
                </th>
                <th rowSpan={2} className="bd1 bdcEDEDED bgcF1F8FF taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc2c3e50 wsNw">조합비총액</span>
                </th>
                <th colSpan={2} className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc2c3e50 wsNw">운영금(본부비)</span>
                </th>
                <th colSpan={5} className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc2c3e50 wsNw">교부금(지방본부비)</span>
                </th>
              </tr>
              <tr className="h30">
                <th className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">%</span>
                </th>
                <th className="bd1 bdcEDEDED bgcE8EEFF taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">금액</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">%</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">금액</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs12 fc777777 wsNw">집중국 인원</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">집중국 금액</span>
                </th>
                <th className="bd1 bdcEDEDED bgcfcf1e1 taC fw700">
                  <span className="dpFlx aiC jcC fs13 fc777777 wsNw">총배정액</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {dataArr.length === 0 ? (
                <tr>
                  <td colSpan={9} className="bd1 bdcEDEDED">
                    <span className="h44 dpFlx aiC jcC fc999999 fm-c fs13">조회된 데이터가 없습니다.</span>
                  </td>
                </tr>
              ) : (
                dataArr.map((row: any, index: number) => {
                  const zebraBg = index % 2 === 0 ? "bgcWhite" : "bgcF9F9F9";
                  return (
                    <tr key={index} className={zebraBg}>
                      {/* 지방본부 */}
                      <td className="bd1 bdcEDEDED">
                        <span className="dpFlx aiC pw12 fm-c fs13 fc333333 wsNw ofH toE">{row.grnamtOrgNm}</span>
                      </td>

                      {/* 조합비총액(입력) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.mbsdsSumAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30 bd1 bdrd4"
                            inputClass="taR pr5"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            onChange={(data: string) => onChangeMbsdsSumAmt(index, data)}
                          />
                        </div>
                      </td>

                      {/* 운영% (고정/표시만) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcC pw6 fm-b fs13 fc333333">{row.operRt}</div>
                      </td>

                      {/* 운영금액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.operAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            disabled={true}
                          />
                        </div>
                      </td>

                      {/* 교부% (고정/표시만) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcC pw6 fm-b fs13 fc333333">{row.dtbnRt}</div>
                      </td>

                      {/* 교부금액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.dtbnAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            disabled={true}
                          />
                        </div>
                      </td>

                      {/* 집중국 인원 (입력) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcC pw12">
                          <NumberTextBox
                            value={row.dtbnNopeCnt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30 bd1 bdrd4"
                            inputClass="taC"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            onChange={(data: string) => onChangeDtbnNopeCnt(index, data)}
                          />
                        </div>
                      </td>

                      {/* 집중국 금액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <div className="dpFlx aiC jcE pw12">
                          <NumberTextBox
                            value={row.dtbnPstofcAmt}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            placeholder="0"
                            className="w100p h30"
                            inputClass="taR pr10"
                            fontSize={13}
                            fontFamily="fm-b"
                            letterSpacing={-0.6}
                            disabled={true}
                          />
                        </div>
                      </td>

                      {/* 총배정액 (자동/읽기) */}
                      <td className="bd1 bdcEDEDED">
                        <span className="dpFlx aiC jcE pw12 fm-b fs13 fc333333">
                          {Util.comma(row.dtbnSumAmt)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}

              {/* 합계 row */}
              <tr className="bgcF1F8FF" style={{ height: "38px" }}>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcC pw12 fm-c fs13 fc111111">합계</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcE pr17 fm-c fs13 fc111111">{Util.comma(sumMbsdsSumAmt)}</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcC pr17 fm-c fs12 fc666666"></span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcE pr21 fm-c fs13 fc111111">{Util.comma(sumOperAmt)}</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcC pr17 fm-c fs12 fc666666"></span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcE pr21 fm-c fs13 fc111111">{Util.comma(sumDtbnAmt)}</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcC fm-c fs13 fc111111">{Util.comma(sumDtbnNopeCnt)}명</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcE pr21 fm-c fs13 fc111111">{Util.comma(sumDtbnPstofcAmt)}</span>
                </td>
                <td className="bd1 bdcEDEDED">
                  <span className="dpFlx aiC jcE pr12 fm-c fs13 fc007bff">{Util.comma(sumDtbnSumAmt)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 버튼 */}
        <div className="dpFlx aiC jcC gap10 mt20">
          <Button
            buttonName="취소"
            width={120}
            height={40}
            fontFamily="fm-b"
            fontSize={15}
            letterSpacing={-0.8}
            variant="styCancelBtn"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            onClickFunc={closeModal}
          />
          <Button
            buttonName="저장"
            width={120}
            height={40}
            fontFamily="fm-b"
            fontSize={15}
            letterSpacing={-0.8}
            color="#fff"
            backgroundColor="#007bff"
            borderRadius={6}
            buttonClass="dpFlx aiC jcC"
            onClickFunc={save}
          />
        </div>
      </div>
    </div>
  );
};

export default BgtOvrlEmpGrnamtDialog;
